import { useAuth0 } from '@auth0/auth0-react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../states/user.atom';
import { useMe } from '../../hooks/use-me.hook';
import SpinPage from '../../components/common/SpinPage';
import { User } from '../../components/dashboard/admin/users/types/user.types';
import ErrorFallback from '../common/ErrorFallback';
import { useTranslation } from 'react-i18next';
import { useActiveTenant } from '@states/active-tenant.atom';
import { Tenant } from '@components/dashboard/sysadmin/sysadmin-tenant/types/tenant.types';
import { useBrandingColors } from '@states/theme.atom';
import { activateTenant } from '@utils/tenant.util';

interface RequireAuthProps extends PropsWithChildren {
  onUserFetched?: (userData: User) => void;
}

export function RequireAuth({ children, onUserFetched }: RequireAuthProps) {
  const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently, logout } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeUser, setActiveUser } = useAuthUser();
  const [hasToken, setHasToken] = useState(false);
  const { t } = useTranslation();
  const { setActiveTenant } = useActiveTenant();
  const { setBrandingColors } = useBrandingColors();

  // Check for token
  useEffect(() => {
    const checkToken = async () => {
      if (isAuthenticated && !isAuthLoading) {
        try {
          const token = await getAccessTokenSilently();
          setHasToken(!!token);
        } catch (error) {
          console.error('Failed to get access token:', error);
          setHasToken(false);
        }
      } else {
        setHasToken(false);
      }
    };
    checkToken();
  }, [isAuthenticated, isAuthLoading, getAccessTokenSilently, navigate]);

  const {
    data: userData,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
  } = useMe({
    enabled: isAuthenticated && !isAuthLoading && hasToken,
    retry: false, // Disable automatic retries
    retryOnMount: false, // Prevent retry on component mount
  });

  // Store intended route if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthLoading, isAuthenticated, location, navigate]);

  // Set user and clear intended route after login
  useEffect(() => {
    if (userData) {
      setActiveUser(userData.data);
      onUserFetched?.(userData.data);
      if (!userData.data.isSysAdmin && userData.data.tenantId) {
        activateTenant(userData.data.tenantId as Tenant, {
          setActiveTenant,
          setBrandingColors,
        });
      }
    }
  }, [isAuthenticated, userData, setActiveUser, onUserFetched]);

  if (isUserError) {
    // For 401 errors, we should logout the user
    if (userError && 'status' in userError && userError.status === 401) {
      return (
        <ErrorFallback
          error={userError}
          resetErrorBoundary={() => {
            logout({ logoutParams: { returnTo: window.location.origin } });
          }}
          message={t('common.sessionExpired')}
          details={t('common.sessionExpiredDetails')}
          resetText={t('common.logInAgain')}
        />
      );
    }

    // For other errors
    return (
      <ErrorFallback
        error={userError}
        resetErrorBoundary={() => {
          navigate('/login', { replace: true });
        }}
        message={t('common.errorLoadingData')}
        details={t('common.errorLoadingUserDataDetails')}
      />
    );
  }

  if (isAuthLoading || isUserLoading || !activeUser) {
    return <SpinPage />;
  }

  return <>{children}</>;
}
